import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AGENTS, type Agent } from '../../agents/registry.ts';
import { isAgentEnabledFor } from '../../agents/agentAccess.ts';
import { setLastAgent } from '../../agents/preferences.ts';
import ChatPanel from '../../components/console/ChatPanel.tsx';
import ConsoleTopbar from '../../components/console/ConsoleTopbar.tsx';
import InspectorPanel from '../../components/console/InspectorPanel.tsx';
import MobileNav, { type ConsoleView } from '../../components/console/MobileNav.tsx';
import SessionsPanel from '../../components/console/SessionsPanel.tsx';
import { useAgentChat } from '../../components/console/useAgentChat.ts';
import { useAgentMemory } from '../../components/console/useAgentMemory.ts';
import { useMemoryGraph } from '../../components/console/useMemoryGraph.ts';
import { useSessionLog } from '../../components/console/useSessionLog.ts';
import { logToGraph } from '../../components/console/sessionLogProjections.ts';
import { useSessions } from '../../components/console/useSessions.ts';
import { useRunResultDelivery } from '../../components/console/useRunResultDelivery.ts';
import { useUserFiles } from '../../components/console/useUserFiles.ts';
import { useFileUpload } from '../../components/console/useFileUpload.ts';
import { useJobsData } from '../../components/console/useJobsData.ts';
import {
  useWorkflowRunProgress,
  type WorkflowProgressStreamEvent,
} from '../../components/console/useWorkflowRunProgress.ts';
import { getDownloadUrl } from '../../agents/userFilesApi.ts';
import { toolsForAgent } from '../../agents/toolCatalog.ts';
import { canUseMcpSettings, MCP_AGENT_SLUG } from '../../agents/mcpApi.ts';
import { submitFeedback } from '../../agents/feedbackApi.ts';
import { shouldShowAds } from '../../agents/ads.ts';
import AdSlot from '../../components/AdSlot.tsx';
import { useBannerReveal } from '../../components/console/useBannerReveal.ts';
import { useAuth } from '../../auth/useAuth.ts';
import './AgentConsole.css';

/**
 * The in-app agent console (`/agent/:slug`) — a React port of the
 * `agent-interface` mock. Only available agents have a console; others bounce
 * back to the chooser. The validated agent is handed to an inner component so
 * the simulation hooks run on a stable, present agent.
 */
export default function AgentConsole() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  // Per-user eligibility (group-gated agents like Vera included); the agents
  // runtime enforces the same gate authoritatively (FR-ACCESS-4).
  if (!slug || !isAgentEnabledFor(slug, user)) {
    return <Navigate to="/app" replace />;
  }
  return <Console agent={AGENTS[slug]} />;
}

function Console({ agent }: { agent: Agent }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { api, counts } = useMemoryGraph(canvasRef, popRef);
  // On an expired session the chat routes the user back to sign-in (the agent
  // 401 → `expireSession` flips auth state → <RequireAuth> redirects).
  const { user, expireSession } = useAuth();
  // Owns session state; feeds the current id + restored transcript into the
  // chat and the history list + actions into the Sessions panel.
  const sessions = useSessions(agent);
  // Owns the user's document tree for the Files panel; reloaded when a chat turn
  // creates or deletes a document (see `onDocumentsChanged` below).
  const files = useUserFiles();
  // Jobs section (U12/U13): only agents with workflow tools get it —
  // manifest-driven, so Uno's console is untouched until he gains them.
  const agentTools = toolsForAgent(agent.slug);
  const hasWorkflows = agentTools.some((t) => t.name === 'workflow_run');
  // MCP surface (M-A2): the Tools panel shows the "Connected Tools (MCP)" group
  // only for the MCP agent (Maximus) and an eligible user (beta + admin).
  const showMcp = agent.slug === MCP_AGENT_SLUG && canUseMcpSettings(user);
  const jobs = useJobsData(hasWorkflows);
  // Ads show for guest + free users only (never paid), on deployed stages.
  // No provider is currently wired, so AdSlot renders nothing — the placement
  // is kept so a replacement network is a one-file change (see AdSlot.tsx).
  const showAds = shouldShowAds(user);
  // First-run vs returning greeting: any prior session with turns means the
  // user has chatted with this agent before. `undefined` while history loads
  // so the greeting doesn't assert "first run" prematurely; the most recent of
  // those threads seeds the "pick that up?" nudge.
  const priorThreads = sessions.sessions.filter((s) => s.turnCount > 0);
  const returning = sessions.loading ? undefined : priorThreads.length > 0;
  const lastTitle =
    [...priorThreads].sort((a, b) => b.lastTurnAt.localeCompare(a.lastTurnAt))[0]?.title ||
    undefined;
  // The "Memory › All" list reads the agent's stored memory (the knowledge base)
  // from the reachable `/agent-memory/{agentId}` Lambda — distinct from the
  // graph's source (the session log) by design. Session-scoped so the current
  // session's turns show under "Session" and are excluded from "Episodic" (the
  // other sessions) rather than listed twice. `applyMemoryWrite` keeps it current
  // as turns stream (declared before `useAgentChat` to feed the callback into it).
  const { sections: memorySections, applyMemoryWrite } = useAgentMemory(
    agent.slug,
    sessions.currentSessionId,
  );
  // The single source for the Memory Graph: the lossless session log. Live turns
  // append to it (via `useAgentChat`); a restore rehydrates it from the saved
  // transcript. The graph is a pure projection (`logToGraph`, below).
  const log = useSessionLog(sessions.currentSessionId, sessions.transcript, sessions.restoreKey);
  // Latest live `workflow-progress` frame from the chat stream — the primary
  // source for the run card while a workflow executes (declared before
  // `useAgentChat` to feed the callback into it).
  const [wfStreamEvent, setWfStreamEvent] = useState<WorkflowProgressStreamEvent | null>(null);
  // Daily-usage snapshot from each turn's terminal frame — surfaced as a
  // small allowance chip so a signed-in user can see their budget before
  // hitting the cap (user request; the guest console has the equivalent).
  const [usage, setUsage] = useState<{
    used: number;
    limit: number;
    remaining: number;
    omUsedToday?: number;
    omBudget?: number;
  } | null>(null);
  const {
    items,
    toolLog,
    status,
    activity,
    currentSessionId,
    sendMessage,
    retryMessage,
    setFeedback,
    beginUpload,
    setUploadProgress,
    completeUpload,
    failUpload,
  } = useAgentChat(
    agent,
    log,
    expireSession,
    {
      sessionId: sessions.currentSessionId,
      transcript: sessions.transcript,
      restoreKey: sessions.restoreKey,
      firstName: user?.givenName,
      returning,
      lastTitle,
      onDocumentsChanged: files.reload,
      // A queued image job = new C7 record: surface the "Generating…" row in
      // Jobs (which then polls to completion) and the job.json file in Files.
      onImageJobCreated: () => {
        jobs.reload();
        files.reload();
      },
      onMemoryWrite: applyMemoryWrite,
      onWorkflowProgress: setWfStreamEvent,
      onUsageLimit: (u) =>
        setUsage({
          used: u.used,
          limit: u.limit,
          remaining: u.remaining,
          ...(u.omUsedToday !== undefined ? { omUsedToday: u.omUsedToday } : {}),
          ...(u.omBudget !== undefined ? { omBudget: u.omBudget } : {}),
        }),
      // Drives the "preparing" placeholder so the greeting renders once session
      // state settles, instead of flickering first-run → "welcome back".
      loading: sessions.loading,
      // Switches that placeholder's copy to "Creating…" for the New-session action.
      creating: sessions.creating,
    },
  );

  // ---- Console file uploads -------------------------------------------------
  //
  // The transcript card created when the user picked a file. A ref, not state:
  // the upload callbacks must read the current value without re-subscribing,
  // and the card id is the only thread tying a pick to its completion.
  const uploadIdRef = useRef<number | null>(null);

  const upload = useFileUpload({
    onComplete: (file) => {
      const id = uploadIdRef.current;
      if (id === null) {
        return; // nothing was picked in this session — nothing to complete
      }
      uploadIdRef.current = null;
      completeUpload(id, file);
      files.reload(); // the Files panel now has a new row, with its expiry chip
    },
  });

  const onPickFile = (picked: File): void => {
    // Ask the hook FIRST. It runs one upload at a time, so a second pick while
    // one is in flight is refused — and creating the card before finding that
    // out would leave a card stuck at "uploading" forever, with the ref
    // pointing at it instead of the upload that is actually running.
    if (!upload.start(picked)) {
      return;
    }
    uploadIdRef.current = beginUpload(picked.name);
  };

  // Mirror transfer progress and failures onto the card the pick created.
  const uploadState = upload.state;
  useEffect(() => {
    const id = uploadIdRef.current;
    if (id === null) {
      return;
    }
    if (uploadState.status === 'uploading') {
      setUploadProgress(id, uploadState.progress);
    } else if (uploadState.status === 'error') {
      failUpload(id, uploadState.message);
      uploadIdRef.current = null;
    }
    // `setUploadProgress`/`failUpload` are stable across renders (defined in the
    // hook body, not memoised) — the state transition is the only trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadState]);

  // Live run visibility (user feedback): while workflow_run executes, the
  // stream's workflow-progress frames drive the step checklist + intermediate
  // results; REST polling covers reloads and dead streams.
  const runProgress = useWorkflowRunProgress(toolLog, hasWorkflows, wfStreamEvent);
  // Deliver a finished run's RESULT into the chat. A workflow outlives the turn
  // that started it by minutes, so the result arrives over the transcript
  // reload rather than the (long since closed) stream — see the hook for the
  // full chain and the deferral bug it fixes.
  useRunResultDelivery({
    record: runProgress.progress?.record,
    chatStatus: status,
    sessionId: sessions.currentSessionId,
    reload: sessions.reloadCurrent,
    onRunSettled: files.reload,
  });
  // Project the session log onto the memory graph — the graph reconciles to the
  // log (prompt → tool → written + recalled records, latest turn lit). The ONLY
  // writer of the graph; reruns whenever the log grows or a session is restored.
  useEffect(() => {
    logToGraph(api, log.events);
  }, [api, log.events]);

  const [collapsed, setCollapsed] = useState(false);
  const [view, setView] = useState<ConsoleView>('chat');
  // One-shot message handed over by another route (e.g. the Workflow
  // Details page's Run button): send it as the user's turn once, then strip
  // the router state so a refresh/back never re-runs it.
  const location = useLocation();
  const autoMessageFired = useRef(false);
  useEffect(() => {
    const autoMessage = (location.state as { autoMessage?: string } | null)?.autoMessage;
    if (typeof autoMessage === 'string' && autoMessage.length > 0 && !autoMessageFired.current) {
      autoMessageFired.current = true;
      sendMessage(autoMessage);
      setView('chat');
      window.history.replaceState({}, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on mount-state
  }, []);
  // Mobile brand-banner reveal-on-scroll, watching every panel (see hook).
  const bannerHidden = useBannerReveal(view);

  // Mobile: the bottom tab bar switching to Jobs is the panel "becoming
  // visible" — refresh still-generating image jobs the moment it shows.
  // (Desktop uses the accordion's onJobsShown; the hook no-ops when every
  // job is terminal, so extra fires are free.)
  const { refreshImageJobs } = jobs;
  useEffect(() => {
    if (view === 'jobs') {
      refreshImageJobs();
    }
  }, [view, refreshImageJobs]);

  // Same for Files (desktop uses the accordion's onFilesShown): the tree is
  // fetched on mount and refreshed by `onDocumentsChanged`, which only fires
  // for `doc_*` tools on the live stream. Anything else that writes a file — a
  // workflow step, a tool the chat never saw settle — would otherwise stay
  // invisible until a full page reload.
  const reloadFiles = files.reload;
  useEffect(() => {
    if (view === 'files') {
      reloadFiles();
    }
  }, [view, reloadFiles]);

  // Picking a session (or starting a new one) jumps straight to the chat: on
  // mobile the bottom tab bar drives which single panel is visible, so this is
  // what brings the restored/empty conversation into focus. On desktop the
  // view switch is a no-op (all panels are always visible). The restored
  // transcript then re-keys useAgentChat, whose item rebuild auto-scrolls the
  // chat to the latest message.
  const selectSession = (sessionId: string) => {
    sessions.selectSession(sessionId);
    setView('chat');
  };
  const newSession = () => {
    sessions.newSession();
    setView('chat');
  };

  // Opening a file downloads it DIRECTLY — the Files Lambda mints the presigned
  // URL and we navigate to it (objects carry Content-Disposition: attachment, so
  // the browser downloads). This bypasses the agent, which proved unreliable at
  // minting share links (it would skip the tool and reuse a stale/fabricated URL
  // from context). On any failure we fall back to asking the agent in chat.
  const openFile = (path: string) => {
    void (async () => {
      try {
        const { url } = await getDownloadUrl(path);
        const a = document.createElement('a');
        a.href = url;
        a.download = path.split('/').pop() ?? 'document';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch {
        sendMessage(`Please share a download link for "${path}".`);
        setView('chat');
      }
    })();
  };
  // Deleting still goes through chat: the agent surfaces an in-chat Yes/No
  // confirm before anything is removed (the confirmation needs the agent).
  const deleteFile = (path: string) => {
    sendMessage(`Please delete "${path}".`);
    setView('chat');
  };

  // Thumbs up/down on an agent message: stamp the choice optimistically (which
  // locks the buttons), then POST the rating + conversation for diagnosis. On a
  // failed POST, clear the choice so the user can retry.
  const onFeedback = (messageId: number, rating: 'up' | 'down') => {
    setFeedback(messageId, rating);
    void submitFeedback({
      rating,
      agentId: agent.slug,
      sessionId: currentSessionId,
      messageId,
      items,
    }).catch(() => setFeedback(messageId, undefined));
  };

  // Remember this agent so the signed-in home redirect returns the user here.
  useEffect(() => {
    setLastAgent(agent.slug);
  }, [agent.slug]);

  return (
    <div className={bannerHidden ? 'om-console topbar-hidden' : 'om-console'} data-mview={view}>
      <ConsoleTopbar />
      <div className={collapsed ? 'om-body is-collapsed' : 'om-body'}>
        <SessionsPanel
          agent={agent}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((c) => !c)}
          sessions={sessions.sessions}
          currentSessionId={sessions.currentSessionId}
          loading={sessions.loading}
          error={sessions.error}
          onNewSession={newSession}
          onSelectSession={selectSession}
          onDeleteSession={sessions.deleteSession}
          onRetry={sessions.retry}
        />
        <ChatPanel
          agent={agent}
          onPickFile={onPickFile}
          onCancelUpload={upload.cancel}
          items={items}
          status={status}
          activity={activity}
          onSend={sendMessage}
          onRetry={retryMessage}
          onFeedback={onFeedback}
          onNewSession={newSession}
          onChooseAgent={() => navigate('/app')}
          usage={usage}
          runProgress={runProgress.progress}
          onRunProgressDismiss={runProgress.dismiss}
          {...(showAds ? { topSlot: <AdSlot placement="chat" /> } : {})}
        />
        <InspectorPanel
          canvasRef={canvasRef}
          popRef={popRef}
          counts={counts}
          toolLog={toolLog}
          memorySections={memorySections}
          tools={agentTools}
          filesTree={files.tree}
          filesLoading={files.loading}
          filesError={files.error}
          onOpenFile={openFile}
          onDeleteFile={deleteFile}
          {...(showMcp ? { mcpSlug: agent.slug } : {})}
          {...(showAds ? { topSlot: <AdSlot placement="panel" desktopOnly /> } : {})}
          showJobs={hasWorkflows}
          jobsWorkflows={jobs.workflows}
          jobsSchedules={jobs.schedules}
          jobsImages={jobs.imageJobs}
          jobsLoading={jobs.loading}
          jobsError={jobs.error}
          jobsCapConflict={jobs.capConflict}
          onOpenWorkflow={(workflowId, scrollToRuns) => {
            navigate(
              `/agent/${agent.slug}/workflows/${workflowId}${scrollToRuns === true ? '#runs' : ''}`,
            );
          }}
          onRunWorkflow={(wf) => {
            // Hand off to Vera in chat — she confirms the prompt cost
            // conversationally and streams the run (approved mockup §3b's
            // fill-in dialog arrives with declared inputs).
            sendMessage(`Run my workflow "${wf.displayName}" (${wf.workflowId}) now.`);
            setView('chat');
          }}
          onToggleSchedule={(jobId, enabled) => {
            void jobs.toggleSchedule(jobId, enabled);
          }}
          onJobsShown={jobs.refreshImageJobs}
          onFilesShown={files.reload}
        />
      </div>
      <MobileNav active={view} onChange={setView} showJobs={hasWorkflows} />
    </div>
  );
}
